import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TuneIcon from '@mui/icons-material/Tune';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import CEFRScaleBar from 'pages/VocabularyTest/components/CEFRScaleBar';
import FilledActionButton from 'components/FilledActionButton';
import { VocabularyTestScope } from 'pages/VocabularyTest/useVocabularyTestScope';
import VocabularyTestScopeSummary from 'pages/VocabularyTest/components/VocabularyTestScopeSummary';
import VocabularyTestScopePanel from 'pages/VocabularyTest/components/VocabularyTestScopePanel';

interface VocabularyTestHowItWorksProps {
  onStart: () => void;
  onToggleScope: () => void;
  scopeOpen: boolean;
  scope: VocabularyTestScope;
  starting?: boolean;
}

interface StepData {
  icon: React.ReactNode;
  title: string;
  hint: string;
}

const STEPS: StepData[] = [
  {
    icon: <ChecklistIcon sx={{ fontSize: 22 }} />,
    title: 'Ти оцінюєш сам',
    hint: 'Просто позначай слова, які знаєш, а які ні — без тестових завдань',
  },
  {
    icon: <ScheduleIcon sx={{ fontSize: 22 }} />,
    title: '~30 хвилин',
    hint: 'Приблизно стільки триває повний прохід — можна робити паузи й продовжувати пізніше',
  },
  {
    icon: <WorkspacePremiumIcon sx={{ fontSize: 22 }} />,
    title: 'Оцінка + рівень',
    hint: 'Отримай розмір словникового запасу, рівень CEFR і план далі',
  },
];

const StepCard: React.FC<StepData> = ({ icon, title, hint }) => (
  <Box
    sx={{
      bgcolor: 'background.default',
      border: '1px solid',
      borderColor: 'primary.tableBorder',
      borderRadius: 1,
      p: '18px 18px 20px',
    }}
  >
    <Stack direction={'row'} alignItems={'center'} spacing={1.5} sx={{ mb: 1.25 }}>
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: (theme) =>
            theme.palette.mode === 'light'
              ? 'rgba(59, 44, 147, 0.10)'
              : 'rgba(155, 124, 255, 0.18)',
          borderRadius: 1,
          color: 'primary.main',
          display: 'inline-flex',
          flex: 'none',
          height: 38,
          justifyContent: 'center',
          width: 38,
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontSize: 16, fontWeight: 700 }}>{title}</Typography>
    </Stack>
    <Typography sx={{ color: 'text.secondary', fontSize: 13.5, lineHeight: 1.45 }}>
      {hint}
    </Typography>
  </Box>
);

const VocabularyTestHowItWorks: React.FC<VocabularyTestHowItWorksProps> = ({
  onStart,
  onToggleScope,
  scopeOpen,
  scope,
  starting,
}) => {
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'primary.tableBorder',
        borderRadius: 2,
        boxShadow: '0 14px 36px -26px rgba(20,20,20,0.4)',
        p: { xs: 2.5, md: '34px 36px' },
      }}
    >
      <Box sx={{ maxWidth: 680, mb: { xs: 2.5, md: '28px' } }}>
        <Typography
          sx={{
            color: 'primary.main',
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: '0.09em',
            mb: 1.25,
            textTransform: 'uppercase',
          }}
        >
          {'Тест словникового запасу'}
        </Typography>
        <Typography
          component={'h1'}
          sx={{
            fontSize: { xs: 24, md: 32 },
            fontWeight: 800,
            lineHeight: 1.15,
            mb: 1.25,
          }}
        >
          {'Дізнайся, скільки англійських слів ти знаєш'}
        </Typography>
        <Typography
          sx={{
            color: 'text.secondary',
            fontSize: { xs: 14, md: 17 },
            lineHeight: 1.5,
          }}
        >
          {
            'Отримай реальну статистику: скільки насправді вивчено слів, які слова перебувають в активному мовленні, які потребують повторення і ще не повністю засвоєні. Тест допоможе швидко наповнити власний Vocabulary і дозволить ефективніше працювати з іншими розділами додатку.'
          }
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: { xs: 1.25, md: 2 },
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          mb: { xs: 2.5, md: '28px' },
        }}
      >
        {STEPS.map((step) => (
          <StepCard key={step.title} {...step} />
        ))}
      </Box>

      <Box
        sx={{
          bgcolor: 'background.default',
          border: '1px dashed',
          borderColor: 'primary.tableBorder',
          borderRadius: 2,
          mb: { xs: 2.5, md: '26px' },
          p: '18px 22px',
        }}
      >
        <CEFRScaleBar count={0} empty />
        <Typography
          sx={{
            color: 'text.secondary',
            fontSize: 13,
            mt: 1.75,
            textAlign: 'center',
          }}
        >
          {'Пройди тест, щоб побачити свою позицію на шкалі'}
        </Typography>
      </Box>

      <Stack
        direction={{ xs: 'column-reverse', sm: 'row' }}
        spacing={1.5}
        sx={{ flexWrap: 'wrap', rowGap: 1.5 }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
        <Button
          variant={'outlined'}
          startIcon={<TuneIcon />}
          onClick={onToggleScope}
          sx={{ height: 46, px: 2.75 }}
        >
          {scopeOpen ? 'Сховати вибір слів' : 'Обрати слова для тесту'}
        </Button>
        <FilledActionButton
          startIcon={<PlayArrowIcon />}
          onClick={onStart}
          disabled={starting}
          sx={{ fontSize: 14, height: 46, px: 2.75 }}
        >
          {'Почати перший тест'}
        </FilledActionButton>
      </Stack>

      {(scopeOpen || !scope.isDefault) && (
        <Box sx={{ mt: 2 }}>
          <VocabularyTestScopeSummary
            scope={scope}
            open={scopeOpen}
            onToggle={onToggleScope}
          />
        </Box>
      )}
      {scopeOpen && (
        <VocabularyTestScopePanel
          scope={scope}
          onClose={onToggleScope}
          onStart={onStart}
          startLabel={'Почати перший тест'}
          starting={starting}
        />
      )}
    </Box>
  );
};

export default VocabularyTestHowItWorks;
